import React, { useEffect, useState } from 'react';
import { Form, Checkbox, Row, Col, theme } from 'antd';

const DEVICE_CONFIG_MODE_FEATURES = [
    'deviceConfig.mode.entranceControl',
    'deviceConfig.mode.cloakroom',
    'deviceConfig.mode.nfcLink',
];

const ModuleClientFeatures = ({ form }) => {
    const features = Form.useWatch('features', form) || [];
    const [termsVisible, setTermsVisible] = useState(false);
    const { token } = theme.useToken();
    const isMobileSelected = features.includes('deviceConfig.mobile');

    const allDevicesFeatures = ['devices.battery','devices.location'];
    const allParticipantFeatures = ['participants.view','participants.details', 'participants.qr',
        'participants.print', 'participants.camera'];
    const allLeadPolicyFeatures = ['leadpolicy.view'];
    const allLeadControlFeatures = ['lead-control.view'];
    const allDeviceConfigTypes = [
        'deviceConfig.mobile',
        'deviceConfig.desktop',
        'deviceConfig.leadcapture',
        ...DEVICE_CONFIG_MODE_FEATURES,
        'deviceConfig.vCard',
        'deviceConfig.design',
    ];

    const dependentsOfView = allParticipantFeatures.filter(
        (f) => f !== "participants.view"
    );

    /* ------------ handler generic ------------- */
    const handleChildrenChange =
        (group) =>
            (checkedValues) => {
                const current = form.getFieldValue("features") || [];
                const others  = current.filter((f) => !group.includes(f));
                form.setFieldsValue({ features: [...others, ...checkedValues] });
            };

    const toggleDeviceConfigFeature = (featureKey, checked) => {
        const current = form.getFieldValue("features") || [];

        if (featureKey === 'deviceConfig.mobile') {
            const nextFeatures = checked
                ? [...new Set([...current, 'deviceConfig.mobile', ...DEVICE_CONFIG_MODE_FEATURES])]
                : current.filter(
                    (feature) =>
                        feature !== 'deviceConfig.mobile' &&
                        !DEVICE_CONFIG_MODE_FEATURES.includes(feature)
                );

            setTimeout(() => {
                form.setFieldsValue({ features: nextFeatures });
            }, 0);
            return;
        }

        const nextFeatures = checked
            ? [...new Set([...current, featureKey])]
            : current.filter((feature) => feature !== featureKey);

        form.setFieldsValue({ features: nextFeatures });
    };


    /* ------------ handle billable checkbox ------------ */
    const handleBillableChange = e => {
        const checked = e.target.checked;
        const current = form.getFieldValue("features") || [];
        if (checked) {
            // If checking, add feature and show terms modal
            form.setFieldsValue({ features: [...current, 'client.billable'] });
            setTermsVisible(true);
        } else {
            // If unchecking, remove feature
            form.setFieldsValue({ features: current.filter(f => f !== 'client.billable') });
        }
    };

    /* ------------ dependency effect -------- */
    useEffect(() => {
        const anyDependentSelected = features.some((f) =>
            dependentsOfView.includes(f)
        );

        if (anyDependentSelected && !features.includes("participants.view")) {
            form.setFieldsValue({ features: [...features, "participants.view"] });
        }
    }, [features]);

    useEffect(() => {
        if (isMobileSelected) return;

        const nextFeatures = features.filter(
            (feature) => !DEVICE_CONFIG_MODE_FEATURES.includes(feature)
        );

        if (nextFeatures.length !== features.length) {
            form.setFieldsValue({ features: nextFeatures });
        }
    }, [features, form, isMobileSelected]);

    return (
        <>
        {/* Checkbox for client.billable */}
        <Form.Item name="features" noStyle>
            <Checkbox
                checked={features.includes('client.billable')}
                onChange={handleBillableChange}
                style={{ marginBottom: 16 }}
            >
                Billable customer (show the modal for acceptance of terms in device transfer).
            </Checkbox>
        </Form.Item>
        <Form.Item label="Features" name="features">
            <Checkbox.Group value={features}>
                <Row gutter={24}>
                    {/* DEVICE CONFIG TYPES COLUMN */}
                    <Col span={6}>
                        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Device Config</div>

                        <Row style={{ paddingLeft: 24 }}>
                            {(() => {
                                const baseItems = allDeviceConfigTypes.filter((f) => {
                                    const parts = f.split(".");
                                    return parts.length === 2; // deviceConfig.mobile, deviceConfig.desktop...
                                });

                                const modeItems = DEVICE_CONFIG_MODE_FEATURES;

                                return (
                                    <>
                                        {/* Base items (mobile/desktop/leadcapture) */}
                                        {baseItems.map((f) => {
                                            const label = f.split(".")[1].replace(/^\w/, (c) => c.toUpperCase());
                                            const isMobileConfig = f === 'deviceConfig.mobile';

                                            return (
                                                <Col span={24} key={f}>
                                                    <Checkbox
                                                        value={f}
                                                        checked={features.includes(f)}
                                                        onChange={(e) => toggleDeviceConfigFeature(f, e.target.checked)}
                                                    >
                                                        {label}
                                                    </Checkbox>

                                                    {isMobileConfig && modeItems.length > 0 && (
                                                        <div
                                                            style={{
                                                                margin: '6px 0 10px 24px',
                                                                padding: '8px 10px',
                                                                borderRadius: 6,
                                                                border: `1px solid ${token.colorBorderSecondary}`,
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    fontSize: 12,
                                                                    fontWeight: 600,
                                                                    color: token.colorTextSecondary,
                                                                    marginBottom: 6,
                                                                    lineHeight: 1.2,
                                                                }}
                                                            >
                                                                Mode
                                                            </div>

                                                            <Row>
                                                                {modeItems.map((f) => (
                                                                    <Col span={24} key={f}>
                                                                        <Checkbox
                                                                            value={f}
                                                                            checked={features.includes(f)}
                                                                            disabled={!isMobileSelected}
                                                                            onChange={(e) => toggleDeviceConfigFeature(f, e.target.checked)}
                                                                        >
                                                                            {f
                                                                                .split(".")[2]
                                                                                .replace(/^\w/, (c) => c.toUpperCase())
                                                                                .replace(/([a-z])([A-Z])/g, '$1 $2')}
                                                                        </Checkbox>
                                                                    </Col>
                                                                ))}
                                                            </Row>
                                                        </div>
                                                    )}
                                                </Col>
                                            );
                                        })}
                                    </>
                                );
                            })()}
                        </Row>
                    </Col>
                    {/* DEVICES COLUMN */}
                    <Col span={6}>
                        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Devices</div>
                        <Row style={{ paddingLeft: 24 }}>
                            {allDevicesFeatures.map((f) => (
                                <Col span={24} key={f}>
                                    <Checkbox value={f} onChange={() =>
                                        handleChildrenChange(allDevicesFeatures)(
                                            features.includes(f)
                                                ? features.filter(x => x !== f)
                                                : [...features, f]
                                        )
                                    }>
                                        {f.split('.')[1].replace(/^\w/, (c) => c.toUpperCase())}
                                    </Checkbox>
                                </Col>
                            ))}
                        </Row>
                    </Col>
                    {/* PARTICIPANTS COLUMN */}
                    <Col span={6}>
                        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Participants</div>
                        <Row style={{ paddingLeft: 24 }}>
                            {allParticipantFeatures.map((f) => (
                                <Col span={24} key={f}>
                                    <Checkbox value={f} onChange={() =>
                                        handleChildrenChange(allParticipantFeatures)(
                                            features.includes(f)
                                                ? features.filter(x => x !== f)
                                                : [...features, f]
                                        )
                                    }>
                                        {f.split('.')[1].replace(/^\w/, (c) => c.toUpperCase())}
                                    </Checkbox>
                                </Col>
                            ))}
                        </Row>
                    </Col>

                    {/* LEAD CONTROL COLUMN */}
                    <Col span={6}>
                        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Lead Control</div>
                        <Row style={{ paddingLeft: 24 }}>
                            {allLeadControlFeatures.map((f) => (
                                <Col span={24} key={f}>
                                    <Checkbox value={f} onChange={() =>
                                        handleChildrenChange(allLeadControlFeatures)(
                                            features.includes(f)
                                                ? features.filter(x => x !== f)
                                                : [...features, f]
                                        )
                                    }>
                                        {f.split('.')[1].replace(/^\w/, (c) => c.toUpperCase())}
                                    </Checkbox>
                                </Col>
                            ))}
                        </Row>
                    </Col>

                    {/* Lead Policy Editor COLUMN */}
                    <Col span={6}>
                        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Lead Policy Editor</div>
                        <Row style={{ paddingLeft: 24 }}>
                            {allLeadPolicyFeatures.map((f) => (
                                <Col span={24} key={f}>
                                    <Checkbox value={f} onChange={() =>
                                        handleChildrenChange(allLeadPolicyFeatures)(
                                            features.includes(f)
                                                ? features.filter(x => x !== f)
                                                : [...features, f]
                                        )
                                    }>
                                        {f.split('.')[1].replace(/^\w/, (c) => c.toUpperCase())}
                                    </Checkbox>
                                </Col>
                            ))}
                        </Row>
                    </Col>
                </Row>
            </Checkbox.Group>
        </Form.Item>
        </>
    );
};

export default ModuleClientFeatures;
